import React, { useEffect, useState } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { convertMoneyToNumber, formatMoneyRoundWithoutDecimals } from '../../util/currency';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, NamedRedirect, LayoutSingleColumn, H3, H4 } from '../../components';
import { languagesHelper as language } from '../../helpers/languages';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import { LoadingPage, ErrorPage, priceData, listingImages } from './BrandListingPage.shared';
const { UUID, Money } = sdkTypes;

import BrandSectionHero from './BrandSectionHero';
import { fetchListingsBrandProducts } from './BrandListingPage.duck';
import classNames from 'classnames';
import BrandProductsCarousel from './BrandProductsCarousel/BrandProductsCarousel';
import css from './BrandListingPage.module.css';

export const BrandListingPageComponent = props => {
  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    config,
    onFetchListingsBrandProducts,
    showListingsBrandProductsError,
    fetchListingsBrandProductsInProgress,
    listingsBrandProducts,
    listingsBrandProductsPagination,
  } = props;

  useEffect(() => {
    if (!listingsBrandProducts?.length) {
      onFetchListingsBrandProducts(listingId, config, 50, 1);
    }
  }, []);

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage />;
  } else if (showListingError || showListingsBrandProductsError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const transactionProcessAlias = publicData?.transactionProcessAlias;
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? ('booking' ? isPurchase : 'purchase') : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');
  const { formattedPrice } = priceData(price, config.currency, intl);
  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const schemaPriceMaybe = price
    ? {
      price: intl.formatNumber(convertMoneyToNumber(price), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      priceCurrency: price.currency,
    }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability =
    currentStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

  const {
    brandLocation,
    certificates,
    delivery,
    importDutiesInfo,
    nrOfEmployees,
    nrOfShops,
    originateCountry,
    productGroup,
    // productPriceRange,
    productPositioning,
    productType,
    returns,
    storeLabels,
    sustainabilityFactors,
    year,
  } = publicData || {};

  // const { minPrice, maxPrice } = productPriceRange;

  let currentLocale = language.get();
  const isDe = currentLocale === "de";

  const listingFieldsConfig = config.listing.listingFields;
  const marketplaceCurrency = config.currency;

  const getLabelsByOptions = (optionValues, listingKey, splitDot = false) => {
    const options = listingFieldsConfig.find(c => c.key === listingKey).enumOptions;
    const labels = optionValues.map(optionValue => {
      const matchingOption = options.find(option => option.option === optionValue);
      const label = isDe && matchingOption?.labelDe ? matchingOption?.labelDe : matchingOption?.label
      return matchingOption ? label : intl.formatMessage({ id: 'EditListingForm.optionNotFound' });
    });

    return splitDot ? labels?.join(' • ') : labels?.join(', ');
  };

  const productPositioningOptions =
    listingFieldsConfig?.find(c => c.key === 'productPositioning').associations || {};
  const formattedProductPositioningArray = productPositioning?.map(
    item => productPositioningOptions?.[item]
  );

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          availability: schemaAvailability,
        },
      }}
    >
      <LayoutSingleColumn className={css.root} topbar={topbar} footer={<FooterContainer />}>
        <BrandSectionHero
          title={title}
          listing={currentListing}
          isOwnListing={isOwnListing}
          onManageDisableScrolling={onManageDisableScrolling}
          noPayoutDetailsSetWithOwnListing={noPayoutDetailsSetWithOwnListing}
        />

        <div className={css.container}>
          <h1 className={css.mainTitle}>
            <span>{title}</span>
          </h1>

          {/* <SectionDisplays /> */}

          <p className={css.detailsSection}>
            <div className={css.leftColumn}>
              <p className={css.section}>
                <strong style={{ fontSize: '21px' }}>
                  <FormattedMessage id="EditBrandPreviewAndSubmitForm.about" />
                </strong>
                <br />
                {description}
              </p>
              <p className={css.section}>
                <div>
                  <strong>
                    <FormattedMessage id="EditBrandPreviewAndSubmitForm.sustainableAndEthicalGuarantees" />
                  </strong>
                  <br />
                  {getLabelsByOptions(sustainabilityFactors, 'sustainabilityFactors', true)}
                </div>
                <div>
                  <strong>
                    <FormattedMessage id="EditBrandPreviewAndSubmitForm.certificates" />
                  </strong>
                  <br />
                  {certificates}
                </div>
              </p>
            </div>

            <div className={css.rightColumn}>
              <p className={css.section}>
                <div className={classNames(css.groups, css.denseGrouping)}>
                  <label className={css.mainLabel}>
                    <FormattedMessage id="EditBrandPreviewAndSubmitForm.basedIn" />
                  </label>
                  {`${brandLocation.city}, ${brandLocation.country}`}
                </div>
                <div className={classNames(css.groups, css.denseGrouping)}>
                  <label className={css.mainLabel}>
                    <FormattedMessage id="EditBrandPreviewAndSubmitForm.establishedIn" />
                  </label>
                  {year}
                </div>
                {nrOfEmployees && (
                  <div className={classNames(css.groups, css.denseGrouping)}>
                    <label className={css.mainLabel}>
                      <FormattedMessage id="EditBrandPreviewAndSubmitForm.nrOfEmployees" />
                    </label>
                    {nrOfEmployees}
                  </div>
                )}
                {nrOfShops && (
                  <div className={classNames(css.groups, css.denseGrouping)}>
                    <label className={css.mainLabel}>
                      <FormattedMessage id="EditBrandPreviewAndSubmitForm.nrOfShops" />
                    </label>
                    {nrOfShops}
                  </div>
                )}
              </p>

              <p className={css.section}>
                <div className={classNames(css.groups, css.denseGrouping)}>
                  <label className={classNames(css.mainLabel, css.fitLabel)}>
                    <FormattedMessage id="EditBrandPreviewAndSubmitForm.productFitLabel" />
                  </label>
                </div>

                {productGroup && (
                  <div className={classNames(css.groups, css.denseGrouping)}>
                    <label className={css.mainLabel}>
                      <FormattedMessage id="StoreDetailsSection.productGroupLabel" />
                    </label>
                    {/* {productGroup
                      .map(item => item.charAt(0).toUpperCase() + item.slice(1))
                      .join(', ')} */}
                    {getLabelsByOptions(productGroup, 'productGroup')}
                  </div>
                )}

                {productType && (
                  <div className={classNames(css.groups, css.denseGrouping)}>
                    <label className={css.mainLabel}>
                      <FormattedMessage id="StoreDetailsSection.productCategoryLabel" />
                    </label>
                    {/* {productType
                      .map(item => item.charAt(0).toUpperCase() + item.slice(1))
                      .join(', ')} */}
                    {getLabelsByOptions(productType, 'productType')}
                  </div>
                )}

                {productPositioning && (
                  <div className={classNames(css.groups, css.denseGrouping)}>
                    <label className={css.mainLabel}>
                      <FormattedMessage id="StoreDetailsSection.productPositioningLabel" />
                    </label>
                    {/* {formattedProductPositioningArray
                      .map(item => item.charAt(0).toUpperCase() + item.slice(1))
                      .join(', ')} */}
                    {formattedProductPositioningArray
                      .map(item => (isDe ? item?.labelDe : item?.label))
                      .join(', ')}
                  </div>
                )}

                {storeLabels && (
                  <div className={classNames(css.groups, css.denseGrouping)}>
                    <label className={css.mainLabel}>
                      <FormattedMessage id="StoreDetailsSection.styleFunctionLabel" />
                    </label>
                    {/* {storeLabels
                      .map(item => item.charAt(0).toUpperCase() + item.slice(1))
                      .join(', ')} */}
                    {getLabelsByOptions(storeLabels, 'storeLabels')}
                  </div>
                )}
              </p>
            </div>
          </p>

          <p className={css.section}>
            <div className={classNames(css.groups, css.denseGrouping)}>
              <label className={classNames(css.mainLabel, css.fitLabel)}>
                <FormattedMessage id="EditBrandPreviewAndSubmitForm.deliveryAndReturnsLabel" />
              </label>
            </div>

            <div className={classNames(css.groups, css.denseGrouping)}>
              <label className={css.mainLabel}>
                <FormattedMessage id="EditBrandPreviewAndSubmitForm.sendingFrom" />
              </label>
              {originateCountry}
            </div>

            <div className={classNames(css.groups, css.denseGrouping)}>
              <label className={css.mainLabel}>
                <FormattedMessage id="EditBrandPreviewAndSubmitForm.delivery" />
              </label>
              {delivery}
            </div>

            <div className={classNames(css.groups, css.denseGrouping)}>
              <label className={css.mainLabel}>
                <FormattedMessage id="EditBrandPreviewAndSubmitForm.duties" />
              </label>
              {importDutiesInfo}
            </div>

            <div className={classNames(css.groups, css.denseGrouping)}>
              <label className={css.mainLabel}>
                <FormattedMessage id="EditBrandPreviewAndSubmitForm.returns" />
              </label>
              {returns}
            </div>
          </p>
        </div>
        <BrandProductsCarousel
          brandName={title}
          brandId={listingId}
          intl={intl}
          config={config}
          configLayout={config?.layout?.productImage}
          rentals={listingsBrandProducts}
          pagination={listingsBrandProductsPagination}
          onFetchListingsBrandProducts={onFetchListingsBrandProducts}
          inProgress={fetchListingsBrandProductsInProgress}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

BrandListingPageComponent.defaultProps = {
  currentUser: null,
  showListingError: null,
};

BrandListingPageComponent.propTypes = {
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  showListingError: propTypes.error,
};

const EnhancedBrandListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <BrandListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    showListingsBrandProductsError,
    fetchListingsBrandProductsInProgress,
    listingsBrandProducts,
    listingsBrandProductsPagination,
  } = state.BrandListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    showListingError,
    showListingsBrandProductsError,
    fetchListingsBrandProductsInProgress,
    listingsBrandProducts,
    listingsBrandProductsPagination,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFetchListingsBrandProducts: (brandId, config, limit, page) =>
    dispatch(fetchListingsBrandProducts(brandId, config, limit, page)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const BrandListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedBrandListingPage);

export default BrandListingPage;
